import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'
import {
  AlertTriangle,
  Archive,
  ArchiveRestore,
  ClockAlert,
  Copy,
  FileText,
  FileType2,
  FolderUp,
  Loader2,
  MoreHorizontal,
  Pencil,
  Search,
  Save,
  Sparkles,
  Trash2,
  Upload,
  X,
} from 'lucide-react'
import {
  useDocumentTemplates,
  useDocuments,
  useFolders,
  useIsReadOnly,
  useQuickFillTemplate,
  useDeleteDocument,
  useUpdateTemplate,
  useUpdateDocument,
  useProjects,
  useUploadDocument,
  useWorkspace,
  useWorkspaceContext,
} from '@/api/queries'
import {
  isProcessing,
  MIME_DOCX,
  MIME_PDF,
  progressStageLabel,
  UPLOAD_ACCEPTED_EXTENSIONS,
  validateUploadBatch,
  type DocumentDetail,
  type DocumentStatus,
  type DocumentSummary,
} from '@/api/docfiller-types'
import ErrorFallback from '@/components/ErrorFallback'
import { OnboardingBanner } from '@/components/docfiller/OnboardingBanner'
import { Badge } from '@/components/ui/badge'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { EmptyState } from '@/components/ui/empty-state'
import { Input } from '@/components/ui/input'
import { Kbd } from '@/components/ui/kbd'
import { InlineError } from '@/components/ui/loading-state'
import { PageHeader, SectionHeader } from '@/components/ui/page-header'
import { Progress } from '@/components/ui/progress'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { SavedPromptsManager } from '@/components/docfiller/SavedPromptsManager'
import { WorkspaceContextDialog } from '@/components/docfiller/WorkspaceContextDialog'
import { cn } from '@/lib/utils'
import { formatLocalDateTime } from '@/lib/format-date'

export const Route = createFileRoute('/_authenticated/docfiller/')({
  component: DocfillerIndexPage,
  errorComponent: ErrorFallback,
  validateSearch: parseUploadSearch,
})

const ACCEPT = [
  ...UPLOAD_ACCEPTED_EXTENSIONS,
  MIME_PDF,
  MIME_DOCX,
].join(',')

type UploadFillMode = 'prefill' | 'suggest' | 'do_not_fill'
type DocumentStateFilter = 'active' | 'archived' | 'deleted' | 'expired'

interface UploadSearch {
  workspaceUuid?: string
  projectUuid?: string
  folderUuid?: string
}

function parseUploadSearch(raw: Record<string, unknown>): UploadSearch {
  const workspaceUuid = typeof raw.workspaceUuid === 'string' ? raw.workspaceUuid : undefined
  const projectUuid = typeof raw.projectUuid === 'string' ? raw.projectUuid : undefined
  const folderUuid = typeof raw.folderUuid === 'string' ? raw.folderUuid : undefined

  if (!workspaceUuid) return {}
  if (!projectUuid) return { workspaceUuid }
  return {
    workspaceUuid,
    projectUuid,
    ...(folderUuid ? { folderUuid } : {}),
  }
}

export function DocfillerIndexPage() {
  const navigate = useNavigate()
  const uploadScope = Route.useSearch()
  const [stateFilter, setStateFilter] = useState<DocumentStateFilter>('active')
  const documentFilter = uploadScope.projectUuid
    ? {
        workspaceUuid: uploadScope.workspaceUuid,
        projectUuid: uploadScope.projectUuid,
        folderUuid: uploadScope.folderUuid,
        state: stateFilter,
      }
    : { state: stateFilter }
  const { data: documents, isLoading, error: documentsError } = useDocuments(documentFilter)
  const { data: templates, isLoading: templatesLoading } = useDocumentTemplates({
    workspaceUuid: uploadScope.workspaceUuid,
  })
  const { data: workspaceContext } = useWorkspaceContext()
  const { data: scopedWorkspace } = useWorkspace(uploadScope.workspaceUuid)
  const { data: scopedProjects } = useProjects(uploadScope.workspaceUuid)
  const scopedProject = scopedProjects?.find((p) => p.uuid === uploadScope.projectUuid)
  const { data: scopedFolders } = useFolders(
    uploadScope.workspaceUuid,
    uploadScope.projectUuid,
  )
  const scopedFolder = scopedFolders?.find((f) => f.uuid === uploadScope.folderUuid)
  const upload = useUploadDocument()
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [applyContext, setApplyContext] = useState(true)
  const [uploadAsTemplate, setUploadAsTemplate] = useState(false)
  const [templatePrompt, setTemplatePrompt] = useState('')
  const [fillMode, setFillMode] = useState<UploadFillMode>('suggest')
  const [searchQuery, setSearchQuery] = useState('')
  const hasSavedContext = Boolean(workspaceContext?.content)
  // Read-only users (deactivated accounts) can browse prior documents but
  // can't upload new ones or edit workspace context. ``useIsReadOnly`` reads
  // ``is_active`` off /dashboard/me so the gating tracks the backend's
  // permission state instead of being hardcoded per call site.
  const readOnly = useIsReadOnly()

  const filteredDocs = useMemo(() => {
    if (!documents) return []
    const q = searchQuery.trim().toLowerCase()
    if (!q) return documents
    return documents.filter((d) =>
      documentTitle(d).toLowerCase().includes(q),
    )
  }, [documents, searchQuery])

  const handleFiles = async (files: readonly File[]) => {
    if (readOnly) {
      toast.error('Your account is read-only — uploads are disabled.')
      return
    }
    const result = validateUploadBatch(files)
    if (!result.ok) {
      toast.error(result.message)
      return
    }
    const file = files[0]
    if (!file) return
    try {
      const doc = await upload.mutateAsync({
        file,
        applyContext: hasSavedContext ? applyContext : false,
        isTemplate: uploadAsTemplate,
        templatePrompt,
        fillMode,
        workspaceUuid: uploadScope.workspaceUuid,
        projectUuid: uploadScope.projectUuid,
        folderUuid: uploadScope.folderUuid,
      })
      const uploadedName = documentTitle(doc)
      const uploadMessage = doc.is_template
        ? `Saved ${uploadedName} as a template`
        : doc.initial_context
          ? `Uploaded ${uploadedName} with saved context`
          : `Uploaded ${uploadedName}`
      toast.success(uploadMessage)
      navigate({
        to: '/docfiller/$documentId',
        params: { documentId: doc.uuid },
      })
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Upload failed')
    }
  }

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(false)
    if (readOnly) return
    const files = Array.from(e.dataTransfer.files ?? [])
    if (files.length > 0) void handleFiles(files)
  }

  const isFirstRun = !isLoading && !documentsError && documents?.length === 0

  return (
    <div className="mx-auto w-full max-w-6xl space-y-8 px-4 py-8 md:px-8">
      <PageHeader
        eyebrow="Documents"
        title="Doc Filler"
        description="Drop a PDF or Word doc. The agent extracts the fields, fills them from your context, and gives you a reviewable diff."
        actions={
          <div className="flex items-center gap-2">
            {!readOnly && <SavedPromptsManager />}
            <WorkspaceContextDialog readOnly={readOnly} />
          </div>
        }
      />

      {isFirstRun && <OnboardingBanner />}

      {uploadScope.workspaceUuid && uploadScope.projectUuid && (
        <ScopedUploadBanner
          workspaceName={scopedWorkspace?.name}
          projectName={scopedProject?.name}
          folderName={scopedFolder?.name}
        />
      )}

      {readOnly && (
        <div
          role="status"
          data-testid="docfiller-readonly-banner"
          className="flex items-start gap-2 rounded-md border border-border bg-muted/60 px-3 py-2 text-xs text-muted-foreground"
        >
          <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden />
          <span>
            Your account is read-only. You can browse prior documents but
            uploads and workspace-context edits are disabled.
          </span>
        </div>
      )}

      <section
        className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_18rem]"
        aria-label="Upload"
      >
        <Dropzone
          isDragging={isDragging}
          isUploading={upload.isPending}
          disabled={readOnly}
          onSelectClick={() => {
            if (!readOnly) inputRef.current?.click()
          }}
          onDragEnter={() => setIsDragging(true)}
          onDragLeave={() => setIsDragging(false)}
          onDrop={onDrop}
        >
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            className="sr-only"
            disabled={readOnly}
            onChange={(e) => {
              const files = Array.from(e.target.files ?? [])
              if (files.length > 0) void handleFiles(files)
              e.target.value = ''
            }}
          />
        </Dropzone>

        <UploadAside
          hasSavedContext={hasSavedContext}
          applyContext={applyContext}
          onApplyContextChange={setApplyContext}
          uploadAsTemplate={uploadAsTemplate}
          onUploadAsTemplateChange={setUploadAsTemplate}
          templatePrompt={templatePrompt}
          onTemplatePromptChange={setTemplatePrompt}
          fillMode={fillMode}
          onFillModeChange={setFillMode}
          readOnly={readOnly}
        />
      </section>

      <section className="space-y-3" data-testid="templates-section">
        <SectionHeader
          title="Templates"
          description={
            templates && templates.length > 0
              ? `${templates.length} reusable template${templates.length === 1 ? '' : 's'} ready for quick fill.`
              : 'Upload a document as a template to reuse its fields and fill instructions.'
          }
        />
        {templatesLoading && <DocumentGridSkeleton />}
        {!templatesLoading && templates && templates.length === 0 && (
          <EmptyState
            variant="bare"
            icon={Copy}
            title="No templates yet"
            description="Turn on template mode before uploading a repeated form."
          />
        )}
        {!templatesLoading && templates && templates.length > 0 && (
          <div className="grid gap-3 lg:grid-cols-2">
            {templates.map((template) => (
              <TemplateCard
                key={template.uuid}
                template={template}
                readOnly={readOnly}
              />
            ))}
          </div>
        )}
      </section>

      <section className="space-y-3" data-testid="documents-section">
        <SectionHeader
          title="Documents"
          description={
            documents && documents.length > 0
              ? `${documents.length} document${documents.length === 1 ? '' : 's'} in your workspace.`
              : undefined
          }
          actions={
            documents && documents.length > 0 ? (
              <div className="flex flex-wrap items-center gap-2">
                <DocumentStateFilterControl
                  value={stateFilter}
                  onChange={setStateFilter}
                />
                <div className="relative flex items-center">
                  <Search
                    className="pointer-events-none absolute left-2.5 size-3.5 text-muted-foreground"
                    aria-hidden
                  />
                  <Input
                    type="search"
                    placeholder="Filter…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="h-8 w-36 pl-8 pr-7 text-xs"
                    aria-label="Filter documents by name"
                    data-testid="doc-search-input"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => setSearchQuery('')}
                      className="absolute right-2 text-muted-foreground hover:text-foreground"
                      aria-label="Clear filter"
                      data-testid="doc-search-clear"
                    >
                      <X className="size-3.5" aria-hidden />
                    </button>
                  )}
                </div>
              </div>
            ) : undefined
          }
        />
        {isLoading && <DocumentGridSkeleton />}
        {!isLoading && documentsError && (
          <InlineError
            title="Could not load documents"
            message={
              documentsError instanceof Error
                ? documentsError.message
                : 'Check your connection and try refreshing the page.'
            }
          />
        )}
        {!isLoading && !documentsError && documents && documents.length === 0 && (
          <EmptyState
            icon={FolderUp}
            title="No documents yet"
            description="Drop a PDF or DOCX onto the dropzone above to get started."
            action={
              <button
                type="button"
                className="text-xs font-medium text-primary underline-offset-4 hover:underline"
                onClick={() => inputRef.current?.click()}
              >
                Choose a file to upload
              </button>
            }
          />
        )}
        {!isLoading && !documentsError && documents && documents.length > 0 && filteredDocs.length === 0 && (
          <EmptyState
            variant="bare"
            icon={Search}
            title="No matches"
            description={`No documents match "${searchQuery}".`}
            action={
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="text-xs text-primary underline-offset-2 hover:underline"
              >
                Clear filter
              </button>
            }
            data-testid="docs-no-match"
          />
        )}
        {!isLoading && !documentsError && filteredDocs.length > 0 && (
          <div
            className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3"
            data-testid="document-grid"
          >
            {filteredDocs.map((doc) => (
              <DocumentCard key={doc.uuid} doc={doc} />
            ))}
          </div>
        )}
      </section>
    </div>
  )
}

function ScopedUploadBanner({
  workspaceName,
  projectName,
  folderName,
}: {
  workspaceName?: string
  projectName?: string
  folderName?: string
}) {
  const target = folderName
    ? `${projectName ?? 'this project'} / ${folderName}`
    : projectName ?? 'this project'
  return (
    <div
      role="status"
      data-testid="scoped-upload-banner"
      className="flex items-start gap-2 rounded-md border border-primary/25 bg-primary-soft/45 px-3 py-2 text-sm text-primary-soft-foreground"
    >
      <FolderUp className="mt-0.5 size-4 shrink-0" aria-hidden />
      <span>
        Uploading to <strong>{target}</strong>
        {workspaceName ? (
          <>
            {' '}
            in <strong>{workspaceName}</strong>
          </>
        ) : null}
        . New documents selected here will be saved to this project.
      </span>
    </div>
  )
}

interface DropzoneProps {
  isDragging: boolean
  isUploading: boolean
  disabled?: boolean
  onSelectClick: () => void
  onDragEnter: () => void
  onDragLeave: () => void
  onDrop: (e: React.DragEvent<HTMLDivElement>) => void
  children?: React.ReactNode
}

function Dropzone({
  isDragging,
  isUploading,
  disabled = false,
  onSelectClick,
  onDragEnter,
  onDragLeave,
  onDrop,
  children,
}: DropzoneProps) {
  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled || undefined}
      onClick={disabled ? undefined : onSelectClick}
      onKeyDown={(e) => {
        if (disabled) return
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onSelectClick()
        }
      }}
      onDragOver={(e) => {
        e.preventDefault()
        if (!disabled) onDragEnter()
      }}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      data-dragging={isDragging || undefined}
      data-disabled={disabled || undefined}
      className={cn(
        'group relative flex cursor-pointer flex-col items-center justify-center gap-3 overflow-hidden rounded-xl border border-dashed border-border bg-card/60 px-6 py-14 text-center transition-[background,border-color,box-shadow,transform] duration-200 ease-out',
        'shadow-soft',
        'hover:border-primary/50 hover:bg-card',
        isDragging && 'aurora-glow border-primary bg-primary-soft/60',
        isUploading && 'pointer-events-none opacity-70',
        disabled && 'cursor-not-allowed opacity-60 hover:border-border hover:bg-card/60',
      )}
      data-testid="upload-dropzone"
    >
      <span
        aria-hidden
        className={cn(
          'inline-flex size-12 items-center justify-center rounded-full bg-primary-soft text-primary-soft-foreground transition-transform duration-200 ease-out',
          isDragging && 'scale-110',
        )}
      >
        {isUploading ? (
          <Loader2 className="size-5 animate-spin" />
        ) : (
          <Upload className="size-5" />
        )}
      </span>
      <div className="space-y-1">
        <p className="text-sm font-medium text-foreground">
          {isUploading
            ? 'Uploading…'
            : 'Drop a PDF or Word doc here, or click to choose'}
        </p>
        <p className="text-xs text-muted-foreground">
          Up to 20MB. We support <span className="font-medium">.pdf</span> and{' '}
          <span className="font-medium">.docx</span>.
        </p>
      </div>
      <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
        <span>Or paste with</span>
        <Kbd>⌘</Kbd>
        <Kbd>V</Kbd>
        <span>once the dropzone is focused.</span>
      </div>
      {children}
    </div>
  )
}

interface UploadAsideProps {
  hasSavedContext: boolean
  applyContext: boolean
  onApplyContextChange: (next: boolean) => void
  fillMode: UploadFillMode
  onFillModeChange: (next: UploadFillMode) => void
  uploadAsTemplate: boolean
  onUploadAsTemplateChange: (next: boolean) => void
  templatePrompt: string
  onTemplatePromptChange: (next: string) => void
  readOnly?: boolean
}

function UploadAside({
  hasSavedContext,
  applyContext,
  onApplyContextChange,
  fillMode,
  onFillModeChange,
  uploadAsTemplate,
  onUploadAsTemplateChange,
  templatePrompt,
  onTemplatePromptChange,
  readOnly = false,
}: UploadAsideProps) {
  return (
    <aside className="space-y-3">
      <Card tone="muted" className="border-none">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-sm">
            <Sparkles className="size-4 text-primary" aria-hidden />
            How it works
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 pt-0 text-sm text-muted-foreground">
          <ol className="grid gap-2 sm:grid-cols-3 lg:block lg:space-y-2">
            {[
              'Upload a PDF or DOCX.',
              'The agent parses fields and prefills from your context.',
              'Review the diff inline, then download the filled doc.',
            ].map((step, i) => (
              <li
                key={step}
                className="flex min-w-0 items-start gap-3 rounded-lg border border-border/70 bg-background/55 p-3 text-sm leading-relaxed text-foreground shadow-soft sm:flex-col sm:gap-2 lg:flex-row lg:gap-2 lg:border-0 lg:bg-transparent lg:p-0 lg:text-xs lg:text-muted-foreground lg:shadow-none"
              >
                <span
                  aria-hidden
                  className="mt-0.5 inline-flex size-6 shrink-0 items-center justify-center rounded-full bg-primary text-[11px] font-semibold text-primary-foreground sm:mt-0 lg:size-5 lg:text-[10px]"
                >
                  {i + 1}
                </span>
                <span className="min-w-0">{step}</span>
              </li>
            ))}
          </ol>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-3 p-4" data-testid="upload-fill-mode">
          <div className="space-y-1">
            <p className="text-sm font-medium text-foreground">Fill mode</p>
          </div>
          <div className="grid gap-2">
            {[
              {
                value: 'prefill',
                label: 'Prefill',
                hint: 'Write supported AI values into blank fields.',
              },
              {
                value: 'suggest',
                label: 'Suggest',
                hint: 'Prepare AI values for review before applying.',
              },
              {
                value: 'do_not_fill',
                label: 'Do not fill',
                hint: 'Only extract and name fields.',
              },
            ].map((option) => (
              <label
                key={option.value}
                className={cn(
                  'flex items-start gap-2 rounded-md border p-2.5 text-sm',
                  fillMode === option.value
                    ? 'border-primary bg-primary/5'
                    : 'border-border',
                  readOnly && 'opacity-60',
                )}
              >
                <input
                  type="radio"
                  name="upload-fill-mode"
                  value={option.value}
                  checked={fillMode === option.value}
                  onChange={() => onFillModeChange(option.value as UploadFillMode)}
                  className="mt-1 size-4 accent-primary"
                  disabled={readOnly}
                />
                <span className="space-y-0.5">
                  <span className="block font-medium text-foreground">
                    {option.label}
                  </span>
                  <span className="block text-xs text-muted-foreground">
                    {option.hint}
                  </span>
                </span>
              </label>
            ))}
          </div>
        </CardContent>
      </Card>

      {hasSavedContext && (
        <Card>
          <CardContent
            className="p-4"
            data-testid="apply-context-row"
          >
            <label className="flex items-start gap-2.5 text-sm">
              <input
                id="apply-context-checkbox"
                type="checkbox"
                checked={applyContext}
                onChange={(e) => onApplyContextChange(e.target.checked)}
                className="mt-0.5 size-4 accent-primary"
                data-testid="apply-context-checkbox"
                disabled={readOnly}
              />
              <span className="flex-1 space-y-1">
                <span className="block font-medium text-foreground">
                  Apply my saved context
                </span>
                <span className="block text-xs text-muted-foreground">
                  Pre-populate fields with your saved workspace context. Untick
                  for a one-off upload.
                </span>
              </span>
            </label>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="space-y-3 p-4">
          <label className="flex items-start gap-2.5 text-sm">
            <input
              type="checkbox"
              checked={uploadAsTemplate}
              onChange={(e) => onUploadAsTemplateChange(e.target.checked)}
              className="mt-0.5 size-4 accent-primary"
              disabled={readOnly}
              data-testid="upload-template-checkbox"
            />
            <span className="flex-1 space-y-1">
              <span className="block font-medium text-foreground">
                Save as reusable template
              </span>
              <span className="block text-xs text-muted-foreground">
                Keep this form for repeated quick fills.
              </span>
            </span>
          </label>
          {uploadAsTemplate && (
            <textarea
              value={templatePrompt}
              onChange={(e) => onTemplatePromptChange(e.target.value)}
              className="min-h-24 w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm shadow-xs outline-none focus-visible:ring-[3px] focus-visible:ring-ring/40"
              placeholder="Default fill instructions for every instance"
              disabled={readOnly}
              maxLength={8000}
              data-testid="upload-template-prompt"
            />
          )}
        </CardContent>
      </Card>
    </aside>
  )
}

function TemplateCard({
  template,
  readOnly,
}: {
  template: DocumentSummary
  readOnly: boolean
}) {
  const navigate = useNavigate()
  const [prompt, setPrompt] = useState(template.template_prompt ?? '')
  const [instructions, setInstructions] = useState('')
  const [documentName, setDocumentName] = useState('')
  const updateTemplate = useUpdateTemplate(template.uuid)
  const quickFill = useQuickFillTemplate(template.uuid)

  const savePrompt = async () => {
    try {
      await updateTemplate.mutateAsync({
        is_template: true,
        template_prompt: prompt.trim() || null,
      })
      toast.success('Template prompt saved')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not save template')
    }
  }

  const startQuickFill = async () => {
    try {
      const result = await quickFill.mutateAsync({
        instructions: instructions.trim() || null,
        source_filename: documentName.trim() || null,
      })
      toast.success(`Created ${documentTitle(result.document)}`)
      navigate({
        to: '/docfiller/$documentId',
        params: { documentId: result.document.uuid },
      })
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Quick fill failed')
    }
  }

  return (
    <Card data-testid="template-card">
      <CardHeader>
        <div className="flex items-start gap-3">
          <span
            aria-hidden
            className="mt-0.5 inline-flex size-9 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground"
          >
            <Copy className="size-4" />
          </span>
          <div className="min-w-0 flex-1">
            <CardTitle className="truncate" title={documentTitle(template)}>
              {documentTitle(template)}
            </CardTitle>
            <div className="mt-1 text-xs text-muted-foreground">
              {formatLocalDateTime(template.updated_at)}
            </div>
          </div>
          <StatusBadge doc={template} />
        </div>
      </CardHeader>
      <CardContent className="space-y-3 pt-0">
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          className="min-h-20 w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm shadow-xs outline-none focus-visible:ring-[3px] focus-visible:ring-ring/40"
          placeholder="Template prompt"
          disabled={readOnly}
          maxLength={8000}
          data-testid="template-prompt-input"
        />
        <textarea
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
          className="min-h-20 w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm shadow-xs outline-none focus-visible:ring-[3px] focus-visible:ring-ring/40"
          placeholder="Instance details for this fill"
          disabled={readOnly}
          maxLength={8000}
          data-testid="template-instance-instructions"
        />
        <input
          type="text"
          value={documentName}
          onChange={(e) => setDocumentName(e.target.value)}
          className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm shadow-xs outline-none focus-visible:ring-[3px] focus-visible:ring-ring/40"
          placeholder="Document name (optional - AI generated when blank)"
          disabled={readOnly}
          maxLength={255}
          data-testid="template-document-name"
        />
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => void savePrompt()}
            disabled={readOnly || updateTemplate.isPending}
            className="inline-flex h-8 items-center gap-1.5 rounded-md border border-input bg-background px-3 text-xs font-medium shadow-xs hover:bg-accent disabled:opacity-50"
          >
            {updateTemplate.isPending ? (
              <Loader2 className="size-3.5 animate-spin" aria-hidden />
            ) : (
              <Save className="size-3.5" aria-hidden />
            )}
            Save prompt
          </button>
          <button
            type="button"
            onClick={() => void startQuickFill()}
            disabled={readOnly || quickFill.isPending}
            className="inline-flex h-8 items-center gap-1.5 rounded-md bg-primary px-3 text-xs font-medium text-primary-foreground shadow-xs hover:bg-primary/90 disabled:opacity-50"
          >
            {quickFill.isPending ? (
              <Loader2 className="size-3.5 animate-spin" aria-hidden />
            ) : (
              <Sparkles className="size-3.5" aria-hidden />
            )}
            Quick fill
          </button>
          <Link
            to="/docfiller/$documentId"
            params={{ documentId: template.uuid }}
            className="inline-flex h-8 items-center rounded-md px-3 text-xs font-medium text-muted-foreground hover:text-foreground"
          >
            Open template
          </Link>
        </div>
      </CardContent>
    </Card>
  )
}

function DocumentGridSkeleton() {
  return (
    <div
      className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3"
      aria-busy="true"
      aria-label="Loading documents"
    >
      {Array.from({ length: 6 }).map((_, i) => (
        <Card key={i}>
          <CardHeader>
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-3 w-24" />
          </CardHeader>
          <CardContent className="space-y-2">
            <Skeleton className="h-5 w-28 rounded-full" />
          </CardContent>
        </Card>
      ))}
    </div>
  )
}

const DOCUMENT_STATE_OPTIONS: {
  value: DocumentStateFilter
  label: string
  icon: React.ComponentType<{ className?: string }>
}[] = [
  { value: 'active', label: 'Active', icon: FileText },
  { value: 'archived', label: 'Archived', icon: Archive },
  { value: 'deleted', label: 'Deleted', icon: Trash2 },
  { value: 'expired', label: 'Expired', icon: ClockAlert },
]

function DocumentStateFilterControl({
  value,
  onChange,
}: {
  value: DocumentStateFilter
  onChange: (next: DocumentStateFilter) => void
}) {
  return (
    <div
      className="inline-flex max-w-full overflow-x-auto rounded-md border border-border bg-card p-0.5"
      aria-label="Document state filter"
      data-testid="document-state-filter"
    >
      {DOCUMENT_STATE_OPTIONS.map((option) => {
        const Icon = option.icon
        const selected = option.value === value
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            aria-pressed={selected}
            className={cn(
              'inline-flex h-7 shrink-0 items-center gap-1.5 rounded px-2 text-xs font-medium text-muted-foreground',
              selected && 'bg-primary-soft text-primary-soft-foreground',
            )}
          >
            <Icon className="size-3.5" aria-hidden />
            {option.label}
          </button>
        )
      })}
    </div>
  )
}

function DocumentCard({ doc }: { doc: DocumentSummary }) {
  const Icon = doc.mime_type === MIME_DOCX ? FileType2 : FileText
  const processing = isProcessing(doc.status)
  const updateDocument = useUpdateDocument(doc.uuid)
  const deleteDocument = useDeleteDocument()
  const [renameOpen, setRenameOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [name, setName] = useState(documentTitle(doc))
  const title = documentTitle(doc)
  const showSourceFilename = doc.source_filename !== title
  const rename = async () => {
    const trimmed = name.trim()
    if (!trimmed) return
    try {
      await updateDocument.mutateAsync({ display_name: trimmed })
      toast.success('Document renamed')
      setRenameOpen(false)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not rename document')
    }
  }
  const setArchived = async (archived: boolean) => {
    try {
      await updateDocument.mutateAsync({ archived })
      toast.success(archived ? 'Document archived' : 'Document restored')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not update document')
    }
  }
  const remove = async () => {
    try {
      await deleteDocument.mutateAsync(doc.uuid)
      toast.success('Document deleted')
      setDeleteOpen(false)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not delete document')
    }
  }
  return (
    <>
      <Card
        interactive
        className={cn(
          'aurora-card-hover h-full',
          processing && 'border-primary/30 bg-primary-soft/30',
        )}
        data-testid="document-card"
        data-status={doc.status}
      >
        <CardHeader>
          <div className="flex items-start gap-3">
            <Link
              to="/docfiller/$documentId"
              params={{ documentId: doc.uuid }}
              className="flex min-w-0 flex-1 items-start gap-3 rounded-md outline-none focus-visible:ring-[3px] focus-visible:ring-ring/40"
              data-testid="document-card-link"
            >
              <span
                aria-hidden
                className="mt-0.5 inline-flex size-9 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground"
              >
                <Icon className="size-4" />
              </span>
              <div className="min-w-0 flex-1">
                <CardTitle className="truncate" title={title}>
                  {title}
                </CardTitle>
                {showSourceFilename && (
                  <div className="mt-1 truncate text-xs text-muted-foreground" title={doc.source_filename}>
                    {doc.source_filename}
                  </div>
                )}
                <div className="mt-0.5 text-xs text-muted-foreground">
                  {formatLocalDateTime(doc.created_at)}
                </div>
              </div>
            </Link>
            <DocumentCardMenu
              doc={doc}
              onRename={() => {
                setName(title)
                setRenameOpen(true)
              }}
              onArchive={() => void setArchived(!doc.is_archived)}
              onDelete={() => setDeleteOpen(true)}
              busy={updateDocument.isPending || deleteDocument.isPending}
            />
          </div>
        </CardHeader>
        <CardContent className="space-y-2 pt-0">
          <DocumentStatusIcons doc={doc} />
          {processing && (
            <Progress
              percent={doc.progress_percent ?? null}
              size="sm"
              tone="primary"
              data-testid="card-progress"
            />
          )}
        </CardContent>
      </Card>
      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename document</DialogTitle>
          </DialogHeader>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            aria-label="Document name"
            maxLength={255}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => void rename()} disabled={updateDocument.isPending || !name.trim()}>
              {updateDocument.isPending && <Loader2 className="animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete document?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            This removes {title} from active file views.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={() => void remove()} disabled={deleteDocument.isPending}>
              {deleteDocument.isPending && <Loader2 className="animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

function DocumentCardMenu({
  doc,
  onRename,
  onArchive,
  onDelete,
  busy,
}: {
  doc: DocumentSummary
  onRename: () => void
  onArchive: () => void
  onDelete: () => void
  busy: boolean
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={`Document actions for ${documentTitle(doc)}`}
          disabled={busy || Boolean(doc.is_deleted)}
        >
          <MoreHorizontal />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={onRename}>
          <Pencil className="size-4" />
          Rename
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={onArchive}>
          {doc.is_archived ? (
            <ArchiveRestore className="size-4" />
          ) : (
            <Archive className="size-4" />
          )}
          {doc.is_archived ? 'Restore from archive' : 'Archive'}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem variant="destructive" onSelect={onDelete}>
          <Trash2 className="size-4" />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function documentTitle(doc: DocumentSummary | DocumentDetail): string {
  return doc.display_name?.trim() || doc.source_filename
}

const STATUS_TONE: Record<DocumentStatus, React.ComponentProps<typeof Badge>['tone']> = {
  uploaded: 'info',
  parsing: 'info',
  parsed: 'success',
  filling: 'info',
  filled: 'success',
  error: 'danger',
}

function StatusBadge({ doc }: { doc: DocumentSummary }) {
  const processing = isProcessing(doc.status)

  const label = processing
    ? progressStageLabel(doc.progress_stage)
    : doc.status === 'error' && doc.friendly_error
      ? doc.friendly_error
      : doc.status

  // Long friendly_error strings (e.g. "Looks like a corrupt PDF — failed
  // to parse page tree") were overflowing the card on the 390px-wide
  // mobile viewport because the Badge defaults to `whitespace-nowrap` and
  // `inline-flex`-sizes to its content. `max-w-full` caps the badge at
  // the card width, the `whitespace-normal`/`break-words` pair lets the
  // text wrap (anywhere if needed for a single long token), and
  // `items-start` keeps the leading icon top-aligned once the label spans
  // two lines.
  return (
    <Badge
      tone={STATUS_TONE[doc.status]}
      className="max-w-full items-start whitespace-normal break-words text-left leading-snug"
      data-testid="document-status"
      data-status={doc.status}
      title={typeof label === 'string' ? label : undefined}
    >
      {processing && (
        <Loader2 className="mt-0.5 shrink-0 animate-spin" aria-hidden />
      )}
      {doc.status === 'error' && (
        <AlertTriangle className="mt-0.5 shrink-0" aria-hidden />
      )}
      <span className="min-w-0 break-words">{label}</span>
    </Badge>
  )
}

function DocumentStatusIcons({ doc }: { doc: DocumentSummary }) {
  const chips = [
    doc.is_archived
      ? { key: 'archived', label: 'Archived', icon: Archive, tone: 'neutral' as const }
      : null,
    doc.is_deleted
      ? { key: 'deleted', label: 'Deleted', icon: Trash2, tone: 'danger' as const }
      : null,
    doc.is_expired
      ? { key: 'expired', label: 'Expired', icon: ClockAlert, tone: 'warning' as const }
      : null,
  ].filter((chip): chip is NonNullable<typeof chip> => chip !== null)

  return (
    <div className="flex min-w-0 flex-wrap items-center gap-1.5">
      <StatusBadge doc={doc} />
      {chips.map((chip) => {
        const Icon = chip.icon
        return (
          <Badge
            key={chip.key}
            tone={chip.tone}
            size="xs"
            title={chip.label}
            aria-label={chip.label}
            className="shrink-0"
          >
            <Icon className="size-3" aria-hidden />
            <span className="sr-only sm:not-sr-only">{chip.label}</span>
          </Badge>
        )
      })}
    </div>
  )
}
